/**
 * @unscoped — SystemAdminPage 영역의 i18n — Cycle 69.
 *
 * 적용 범위 (cycle 69):
 *   - TenantDetailPage (22 hardcoded)
 *   - AiGatewayConfigPage (11 hardcoded)
 *
 * 누락 (별도 cycle):
 *   - SystemAdminPage 본체 (cycle 53 의 local SYS_MESSAGES 패턴 — graduation 후보)
 *   - AuditDashboardPage (cycle 51 tAdmin 사용)
 *
 * cycle 51 calendar / cycle 54 mail / cycle 55 onboarding / cycle 57 board /
 * cycle 58 task / cycle 59-60 settings / cycle 61 chat / cycle 68 payroll /
 * cycle 69 sysadmin = 8 신규 모듈.
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 *   - params 치환 ({name}) 지원
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ===== 공통 =====
  'sysadmin.access_denied': {
    ko: '접근 권한이 없습니다',
    en: 'Access denied',
    ja: 'アクセス権限がありません',
  },
  'sysadmin.loading': { ko: '불러오는 중...', en: 'Loading…', ja: '読み込み中...' },

  // ===== TenantDetailPage =====
  'sysadmin.tenant.err_load': {
    ko: '테넌트 불러오기 실패',
    en: 'Failed to load tenant',
    ja: 'テナント読み込み失敗',
  },
  'sysadmin.tenant.not_found': { ko: '테넌트 없음', en: 'Tenant not found', ja: 'テナントなし' },
  'sysadmin.tenant.back_to_list': { ko: '← 목록으로', en: '← Back to list', ja: '← 一覧へ' },
  'sysadmin.tenant.no_domain': { ko: '도메인 없음', en: 'No domain', ja: 'ドメインなし' },
  'sysadmin.tenant.status_section_heading': {
    ko: '상태 및 플래그',
    en: 'Status & flags',
    ja: 'ステータスとフラグ',
  },
  'sysadmin.tenant.col.status': { ko: '상태', en: 'Status', ja: 'ステータス' },
  // Cycle 74 — status 값 라벨 현지화 (TenantDetailPage {t.status} raw 출력 봉합).
  // cycle 73 학습 antipattern-new-transit-status 의 raw-출력 잔여. pending_approval 포함 6 status.
  'sysadmin.tenant.status_value.active': { ko: '활성', en: 'Active', ja: '有効' },
  'sysadmin.tenant.status_value.pending': { ko: '가입 진행 중', en: 'Signing up', ja: '加入中' },
  'sysadmin.tenant.status_value.pending_approval': { ko: '승인 대기', en: 'Awaiting approval', ja: '承認待ち' },
  'sysadmin.tenant.status_value.suspended': { ko: '일시 중지', en: 'Suspended', ja: '一時停止' },
  'sysadmin.tenant.status_value.archived': { ko: '보관됨', en: 'Archived', ja: '保管' },
  'sysadmin.tenant.status_value.frozen': { ko: '동결됨', en: 'Frozen', ja: '凍結' },
  'sysadmin.tenant.col.created_at': { ko: '가입일', en: 'Created at', ja: '登録日' },
  'sysadmin.tenant.col.verified_at': { ko: '검증일', en: 'Verified at', ja: '検証日' },
  'sysadmin.tenant.col.verification_method': {
    ko: '검증 방법', en: 'Verification method', ja: '検証方法',
  },
  'sysadmin.tenant.col.grace': { ko: 'Grace', en: 'Grace', ja: 'Grace' },
  'sysadmin.tenant.col.frozen': { ko: 'Frozen', en: 'Frozen', ja: 'Frozen' },
  'sysadmin.tenant.col.users': { ko: '사용자 수', en: 'Users', ja: 'ユーザー数' },
  'sysadmin.tenant.col.llm_integrations': {
    ko: 'LLM 연동', en: 'LLM integrations', ja: 'LLM連携',
  },
  'sysadmin.tenant.llm_unit': { ko: '개', en: '', ja: '個' },
  'sysadmin.tenant.grace_active': {
    ko: '활성 ({until}까지)',
    en: 'Active (until {until})',
    ja: '有効 ({until}まで)',
  },
  'sysadmin.tenant.grace_done': { ko: '완료', en: 'Done', ja: '完了' },
  'sysadmin.tenant.frozen_at': {
    ko: '동결됨 ({when})',
    en: 'Frozen ({when})',
    ja: '凍結済み ({when})',
  },
  'sysadmin.tenant.frozen_no': { ko: '정상', en: 'Normal', ja: '正常' },
  'sysadmin.tenant.btn_unfreeze': { ko: '🔓 동결 해제', en: '🔓 Unfreeze', ja: '🔓 凍結解除' },
  'sysadmin.tenant.unfreeze_prompt': {
    ko: '동결 해제 사유 (10자 이상):',
    en: 'Unfreeze reason (10+ chars):',
    ja: '凍結解除の理由 (10文字以上):',
  },
  'sysadmin.tenant.err_reason_too_short': { ko: '사유는 10자 이상', en: 'Reason must be 10+ chars', ja: '理由は10文字以上' },
  'sysadmin.tenant.err_unfreeze': {
    ko: '동결 해제 실패', en: 'Unfreeze failed', ja: '凍結解除失敗',
  },

  // ----- TenantDetailPage Quota section -----
  'sysadmin.tenant.quota_heading': { ko: 'Quota', en: 'Quota', ja: 'Quota' },
  'sysadmin.tenant.quota_hint': {
    ko: '빈 값 = SaaS 글로벌 default 사용.',
    en: 'Empty = use SaaS global default.',
    ja: '空欄 = SaaSグローバルのデフォルト使用。',
  },
  'sysadmin.tenant.quota.label_rate_limit': {
    ko: '분당 요청 한도', en: 'Per-minute request limit', ja: '1分あたりのリクエスト上限',
  },
  'sysadmin.tenant.quota.label_token_limit': {
    ko: '일일 LLM 토큰 한도', en: 'Daily LLM token limit', ja: '1日あたりのLLMトークン上限',
  },
  'sysadmin.tenant.quota.placeholder_rate': {
    ko: 'default (60)', en: 'default (60)', ja: 'デフォルト (60)',
  },
  'sysadmin.tenant.quota.placeholder_token': {
    ko: 'default (제한 없음)', en: 'default (no limit)', ja: 'デフォルト (制限なし)',
  },
  'sysadmin.tenant.quota.btn_save': { ko: 'Quota 저장', en: 'Save quota', ja: 'Quota保存' },
  'sysadmin.tenant.err_quota_save': { ko: 'Quota 저장 실패', en: 'Failed to save quota', ja: 'Quota保存失敗' },

  // ----- TenantDetailPage Audit section -----
  'sysadmin.tenant.audit_heading': {
    ko: '최근 audit (10건)',
    en: 'Recent audits (10)',
    ja: '最近のaudit (10件)',
  },
  'sysadmin.tenant.audit_empty': {
    ko: 'audit 기록이 없습니다.',
    en: 'No audit records.',
    ja: 'audit記録がありません。',
  },
  'sysadmin.tenant.audit.col.time': { ko: '시각', en: 'Time', ja: '時刻' },
  'sysadmin.tenant.audit.col.event': { ko: '이벤트', en: 'Event', ja: 'イベント' },
  'sysadmin.tenant.audit.col.actor': { ko: 'actor', en: 'actor', ja: 'actor' },

  // ===== AiGatewayConfigPage =====
  'sysadmin.gateway.back_to_tenants': { ko: '← 테넌트 목록', en: '← Tenant list', ja: '← テナント一覧' },
  'sysadmin.gateway.heading': { ko: 'AI Gateway 환경 점검', en: 'AI Gateway env check', ja: 'AI Gateway 環境チェック' },
  'sysadmin.gateway.subtitle': {
    ko: 'ai-gateway 의 runtime env 요약. INTERNAL_API_TOKEN 은 backend proxy 에서만 사용 — 브라우저에 직접 노출되지 않습니다.',
    en: 'Runtime env summary of ai-gateway. INTERNAL_API_TOKEN is used only by backend proxy and not exposed to the browser.',
    ja: 'ai-gatewayのruntime env要約。INTERNAL_API_TOKENはbackend proxyでのみ使用 — ブラウザに直接露出しません。',
  },
  'sysadmin.gateway.err_load': {
    ko: 'ai-gateway config 불러오기 실패',
    en: 'Failed to load ai-gateway config',
    ja: 'ai-gateway config 読み込み失敗',
  },
  'sysadmin.gateway.err_upstream': {
    ko: ' (upstream {status})',
    en: ' (upstream {status})',
    ja: ' (upstream {status})',
  },
  'sysadmin.gateway.btn_refresh': { ko: '새로고침', en: 'Refresh', ja: '更新' },
  'sysadmin.gateway.validation_section': { ko: '운영 점검', en: 'Validation', ja: '運用チェック' },
  'sysadmin.gateway.validation.prod_passed': {
    ko: '✓ production hard-enforce 통과',
    en: '✓ Production hard-enforce passed',
    ja: '✓ production hard-enforce 通過',
  },
  'sysadmin.gateway.validation.prod_failed': {
    ko: '✗ production hard-enforce 위반',
    en: '✗ Production hard-enforce violated',
    ja: '✗ production hard-enforce 違反',
  },
  'sysadmin.gateway.validation.non_prod': {
    ko: 'ⓘ 비-production 환경',
    en: 'ⓘ Non-production environment',
    ja: 'ⓘ 非-production 環境',
  },
  'sysadmin.gateway.response_time': {
    ko: '응답 시각: {time}',
    en: 'Response time: {time}',
    ja: '応答時刻: {time}',
  },
  'sysadmin.gateway.safe_section': { ko: '일반 환경변수', en: 'Safe env vars', ja: '一般環境変数' },
  'sysadmin.gateway.secret_section': {
    ko: '비밀 환경변수 (마스킹)',
    en: 'Secret env vars (masked)',
    ja: '秘密環境変数 (マスク済み)',
  },
  'sysadmin.gateway.partial_section': { ko: '부분 마스킹', en: 'Partial mask', ja: '部分マスク' },
};

export type SysadminI18nKey = keyof typeof MESSAGES;

/**
 * Cycle 69 — SystemAdminPage 영역 i18n lookup.
 * params 치환 — calendar/workspace/mail/onboarding/board/task/settings/chat/payroll 와 일관.
 */
export function tSysadmin(key: SysadminI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

// base-i18n thin wrapper (cycle 41 통합 패턴 일관)
export function getSysadminLang(): Lang { return getSharedLang(); }
export function setSysadminLang(value: Lang): void { setSharedLang(value); }
export function subscribeSysadminLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
